import {
  getStoryblokImageServiceUrl,
  type SbBlokData,
} from '@cinch-nx/data-storyblok'

import {
  Image as DsImage,
  type ImageAspectRatio,
  type ImageBorderRadius,
  type ImageObjectFit,
} from '../../design-system/image'

/** Storyblok sends '' for a Single-Option left empty */
type Option<T> = T | ''

/** DS_Image offers only some of the fits and radii the Image draws */
type BlokObjectFit = Extract<ImageObjectFit, 'cover' | 'contain'>
type BlokBorderRadius = Extract<
  ImageBorderRadius,
  'none' | 'xs' | 'm' | 'round'
>

/**
 * Every aspect ratio the Image draws, all offered by DS_Image. A Record, so a
 * new ImageAspectRatio fails to compile until it is added here too.
 */
const aspectRatios: Record<ImageAspectRatio, true> = {
  '1/1': true,
  '4/3': true,
  '3/2': true,
  '16/9': true,
}

const objectFits: Record<BlokObjectFit, true> = {
  cover: true,
  contain: true,
}

const borderRadiusOptions: Record<BlokBorderRadius, true> = {
  none: true,
  xs: true,
  m: true,
  round: true,
}

const isOption = <T extends string>(
  options: Record<T, true>,
  value: unknown,
): value is T =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(options, value)

/**
 * Only a listed option is passed on. Anything else becomes undefined, so the
 * Image default applies: the '' of an option left empty, or a value saved
 * before it was removed from the list.
 */
const orDefault = <T extends string>(
  options: Record<T, true>,
  value: unknown,
) => (isOption(options, value) ? value : undefined)

/**
 * Twice the hero's widest image box, so a 3:2 asset stays sharp on 2x screens
 * at the 3/2 and 16/9 ratios. That box is 719px wide, stacked just below
 * 768px, so 720px is doubled. A squarer ratio with the cover fit crops the
 * sides and scales the rest up: by 1.13x at the default 4/3 and by 1.5x at
 * 1/1. One src, without a srcset: every screen gets the same file, which the
 * Image Service resizes and serves as WebP to browsers that accept it.
 */
const imageWidth = 1440

/**
 * The asset's URL on the Image Service. The service enlarges an asset narrower
 * than imageWidth, adding bytes but no detail, so no_upscale keeps that one at
 * its own width. The helper throws for an asset not hosted by Storyblok, which
 * the service cannot serve, so that one keeps its own URL.
 */
const imageSrc = (filename: string) => {
  try {
    const url = getStoryblokImageServiceUrl(filename, { width: imageWidth })

    // The helper's filters do not include no_upscale
    return `${url}/filters:no_upscale()`
  } catch {
    return filename
  }
}

/**
 * Asks the browser to fetch the image before other images. React 18 does not
 * know fetchPriority and warns about the camelCase prop, so the lowercase
 * attribute is passed as it is, as next/image does on React 18. Pass
 * fetchPriority="high" instead once the app is on React 19.
 */
const highPriority = { fetchpriority: 'high' } as object

/** Fields of the DS_Image blok; the option values match the Image props */
export interface ImageBlok extends SbBlokData {
  /**
   * Required in Storyblok, but content saved before a rule change can lack
   * it: its filename is then '' or null, and nothing is drawn. The alt text is
   * set on the asset; one without it is drawn as decorative, rather than having
   * screen readers read out its file name.
   */
  image?: {
    filename?: string | null
    alt?: string | null
  }
  aspectRatio?: Option<ImageAspectRatio>
  objectFit?: Option<BlokObjectFit>
  borderRadius?: Option<BlokBorderRadius>
}

/**
 * Loads eagerly, without loading="lazy", and with a high fetch priority: the
 * blok is only used in the hero, where the image sits above the text on
 * mobile and is the page's largest content (LCP).
 */
export const Image = ({
  image,
  aspectRatio,
  objectFit,
  borderRadius,
}: ImageBlok) =>
  image?.filename ? (
    <DsImage
      src={imageSrc(image.filename)}
      alt={image.alt?.trim() ?? ''}
      aspectRatio={orDefault(aspectRatios, aspectRatio)}
      objectFit={orDefault(objectFits, objectFit)}
      borderRadius={orDefault(borderRadiusOptions, borderRadius)}
      {...highPriority}
    />
  ) : null
